import React, { useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Card, CardBody, CardHeader, Button, Input } from 'reactstrap';
import { Link } from 'react-router-dom';
import { fetchMachines as fetchMachinesAction } from '../actions/machines';
import { addSupportTicket as addSupportTicketAction } from '../actions/supporttickets';
import { getNode, processUserChoice, shouldEscalate, getCurrentLanguage } from '../ai/technicalSupportDecisionTree';
import '../styles/TechnicalSupportChat.css';

const ESCALATION_CATEGORIES = {
  touch_escalate:          'SCREEN_TOUCH',
  power_escalate:          'KIOSC_OFF',
  screen_hardware_escalate:'SCREEN_BREAK',
  screen_software_escalate:'SCREEN_OFF',
  tpv_issue:               'TPV',
  bill_acceptor_issue:     'COINS',
  card_issue:              'TPV',
  bill_issue:              'BILLS',
  other_escalate:          'OTHER',
};

const TechnicalSupportChat = ({ onClose, machineId = null, isModal = true, chatId = null, hideHeader = false, machines = [], fetchMachines, auth = {}, addSupportTicket }) => {
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentNodeId, setCurrentNodeId] = useState('start');
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ticketStatus, setTicketStatus] = useState(null); // null | 'creating' | 'created' | 'error'
  const [selectedMachineSerial, setSelectedMachineSerial] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState('es');
  const [isMobile, setIsMobile] = useState(false);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Load machines if not yet in Redux
  useEffect(() => {
    if (!machines || machines.length === 0) {
      fetchMachines();
    }
  }, []);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect language changes
  useEffect(() => {
    const detectLanguage = () => {
      const lang = getCurrentLanguage();
      setCurrentLanguage(lang);
    };

    detectLanguage();
    
    // Listen for language changes (Google Translate events)
    const interval = setInterval(detectLanguage, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Initialize chat with welcome message - reset when chatId changes
  useEffect(() => {
    const startNode = getNode('start', currentLanguage);
    setConversationHistory([
      {
        id: Date.now(),
        message: startNode.message,
        options: startNode.options,
        isBot: true,
        timestamp: new Date()
      }
    ]);
    setCurrentNodeId('start');
    setUserInput('');
    setTicketStatus(null);
    setSelectedMachineSerial(null);
  }, [currentLanguage, chatId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  const handleOptionClick = async (optionIndex, inputOverride = null) => {
    const currentNode = getNode(currentNodeId, currentLanguage);
    const selectedOption = currentNode.options[optionIndex];
    const effectiveInput = inputOverride !== null ? inputOverride : userInput;

    // Check if this option requires text input
    if (selectedOption.requiresInput && !effectiveInput.trim()) {
      const alertMessages = {
        es: 'Por favor, ingresa la información solicitada.',
        en: 'Please enter the requested information.',
        fr: 'Veuillez saisir les informations demandées.',
        de: 'Bitte geben Sie die angeforderten Informationen ein.',
        it: 'Si prega di inserire le informazioni richieste.',
        pt: 'Por favor, insira as informações solicitadas.'
      };
      alert(alertMessages[currentLanguage] || alertMessages['es']);
      return;
    }

    // Track the machine serial if this is a machine-picker option
    if (selectedOption.inputType === 'machine' && effectiveInput.trim()) {
      setSelectedMachineSerial(effectiveInput.trim());
    }

    setIsLoading(true);

    // Add user's response to conversation
    const userMessage = {
      id: Date.now(),
      message: selectedOption.text,
      additionalInfo: effectiveInput.trim() || null,
      isBot: false,
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, userMessage]);

    // Process the choice and get next node
    const result = processUserChoice(currentNodeId, optionIndex, effectiveInput, currentLanguage);
    
    if (!result) {
      setIsLoading(false);
      return;
    }

    // Small delay for natural conversation feel
    await new Promise(resolve => setTimeout(resolve, 800));

    const nextNode = getNode(result.nextNodeId, currentLanguage);
    
    // Check if we need to escalate to a support ticket
    const escalating = shouldEscalate(result.nextNodeId);

    // Resolve ERP status placeholder for the erp_status_check node
    let botMessageText = nextNode.message;
    if (result.nextNodeId === 'erp_status_check' && botMessageText.includes('{ERP_STATUS_MESSAGE}')) {
      const serial = selectedMachineSerial || effectiveInput.trim();
      const machine = (machines || []).find(m => m.Serial === serial);
      let statusLine;
      if (!machine) {
        const notFound = {
          es: `⚠️ Máquina "${serial}" no encontrada en el sistema.`,
          en: `⚠️ Machine "${serial}" not found in the system.`,
          fr: `⚠️ Machine "${serial}" introuvable dans le système.`,
          de: `⚠️ Gerät "${serial}" nicht im System gefunden.`,
          it: `⚠️ Macchina "${serial}" non trovata nel sistema.`,
          pt: `⚠️ Máquina "${serial}" não encontrada no sistema.`,
        };
        statusLine = notFound[currentLanguage] || notFound.es;
      } else {
        const lastSignal = machine.Last_signal ? Date.parse(machine.Last_signal) : null;
        const diffSeconds = lastSignal ? (Date.now() - lastSignal) / 1000 : null;
        const isOnline = diffSeconds !== null && diffSeconds < 60;
        if (isOnline) {
          const online = {
            es: `✅ Máquina "${serial}" está ONLINE (última señal hace ${Math.round(diffSeconds)}s).`,
            en: `✅ Machine "${serial}" is ONLINE (last signal ${Math.round(diffSeconds)}s ago).`,
            fr: `✅ Machine "${serial}" est EN LIGNE (dernier signal il y a ${Math.round(diffSeconds)}s).`,
            de: `✅ Gerät "${serial}" ist ONLINE (letztes Signal vor ${Math.round(diffSeconds)}s).`,
            it: `✅ Macchina "${serial}" è ONLINE (ultimo segnale ${Math.round(diffSeconds)}s fa).`,
            pt: `✅ Máquina "${serial}" está ONLINE (último sinal há ${Math.round(diffSeconds)}s).`,
          };
          statusLine = online[currentLanguage] || online.es;
        } else {
          const lastSeen = machine.Last_signal
            ? new Date(machine.Last_signal).toLocaleString()
            : '—';
          const offline = {
            es: `❌ Máquina "${serial}" está OFFLINE (última señal: ${lastSeen}).`,
            en: `❌ Machine "${serial}" is OFFLINE (last signal: ${lastSeen}).`,
            fr: `❌ Machine "${serial}" est HORS LIGNE (dernier signal : ${lastSeen}).`,
            de: `❌ Gerät "${serial}" ist OFFLINE (letztes Signal: ${lastSeen}).`,
            it: `❌ Macchina "${serial}" è OFFLINE (ultimo segnale: ${lastSeen}).`,
            pt: `❌ Máquina "${serial}" está OFFLINE (último sinal: ${lastSeen}).`,
          };
          statusLine = offline[currentLanguage] || offline.es;
        }
      }
      botMessageText = botMessageText.replace('{ERP_STATUS_MESSAGE}', statusLine);
    }

    // Add bot's response to conversation
    const botMessage = {
      id: Date.now() + 1,
      message: botMessageText,
      options: nextNode.options,
      isBot: true,
      isEndNode: nextNode.isEndNode,
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, botMessage]);
    setCurrentNodeId(result.nextNodeId);
    setUserInput('');
    setIsLoading(false);
    if (escalating) {
      const category = ESCALATION_CATEGORIES[result.nextNodeId] || 'OTHER';
      const serial = selectedMachineSerial || (selectedOption.inputType === 'machine' ? effectiveInput.trim() : null);
      const machineObj = (machines || []).find(m => m.Serial === serial);

      // Build full conversation log (current history + the bot escalation message just created)
      const fullHistory = [...conversationHistory, botMessage];
      const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const chatResumeLog = fullHistory
        .map(m => `[${fmt(m.timestamp)}] ${m.isBot ? 'Bot' : 'User'}: ${m.message}${m.additionalInfo ? ` (${m.additionalInfo})` : ''}`)
        .join('\n');

      // Build description from user selections only
      const description = fullHistory
        .filter(m => !m.isBot)
        .map(m => m.additionalInfo ? `${m.message}: ${m.additionalInfo}` : m.message)
        .join(' → ');

      setTicketStatus('creating');
      addSupportTicket({
        Category: category,
        Priority: 'HIGH',
        Customer: auth.id,
        ...(machineObj ? { Machine: machineObj._id } : {}),
        OpenAt: new Date(),
        Description: description || undefined,
        ChatResumeLog: chatResumeLog || undefined,
      }).then(response => {
        if (response && response.error) {
          setTicketStatus('error');
        } else {
          setTicketStatus('created');
        }
      }).catch(() => {
        setTicketStatus('error');
      });
    }
  };

  const handleRestart = () => {
    setConversationHistory([]);
    setCurrentNodeId('start');
    setUserInput('');
    setTicketStatus(null);
    setSelectedMachineSerial(null);
    
    const startNode = getNode('start', currentLanguage);
    setConversationHistory([
      {
        id: Date.now(),
        message: startNode.message,
        options: startNode.options,
        isBot: true,
        timestamp: new Date()
      }
    ]);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(currentLanguage === 'es' ? 'es-ES' : currentLanguage === 'en' ? 'en-US' : currentLanguage, { hour: '2-digit', minute: '2-digit' });
  };

  // UI translations
  const uiLabels = {
    es: {
      incidentCreated: 'Incidencia creada exitosamente. Un técnico se pondrá en contacto pronto.',
      enterResponse: 'Escribe tu respuesta aquí...',
      newConsultation: 'Nueva Consulta',
      close: 'Cerrar',
      technicalSupport: 'Soporte Técnico VACWAY',
      autoAssistant: 'Asistente automático de diagnóstico',
      machine: 'Máquina',
      ticketCreating: 'Creando ticket de soporte…',
      ticketCreated: '✅ Ticket creado correctamente. Puedes hacer seguimiento en',
      ticketCreatedLink: 'Soporte → Mis Tickets',
      ticketError: '❌ No se pudo crear el ticket automáticamente. Por favor, crea uno manualmente en Soporte → Mis Tickets.',
    },
    en: {
      incidentCreated: 'Incident created successfully. A technician will contact you soon.',
      enterResponse: 'Type your response here...',
      newConsultation: 'New Consultation',
      close: 'Close',
      technicalSupport: 'VACWAY Technical Support',
      autoAssistant: 'Automatic diagnostic assistant',
      machine: 'Machine',
      ticketCreating: 'Creating support ticket…',
      ticketCreated: '✅ Ticket created successfully. You can track it in',
      ticketCreatedLink: 'Support → My Tickets',
      ticketError: '❌ Could not create the ticket automatically. Please create one manually in Support → My Tickets.',
    },
    fr: {
      incidentCreated: 'Incident créé avec succès. Un technicien vous contactera bientôt.',
      enterResponse: 'Tapez votre réponse ici...',
      newConsultation: 'Nouvelle consultation',
      close: 'Fermer',
      technicalSupport: 'Support technique VACWAY',
      autoAssistant: 'Assistant de diagnostic automatique',
      machine: 'Machine',
      ticketCreating: 'Création du ticket en cours…',
      ticketCreated: '✅ Ticket créé avec succès. Suivez-le dans',
      ticketCreatedLink: 'Support → Mes Tickets',
      ticketError: '❌ Le ticket n’a pas pu être créé automatiquement. Veuillez en créer un manuellement dans Support → Mes Tickets.',
    },
    de: {
      incidentCreated: 'Vorfall erfolgreich erstellt. Ein Techniker wird sich bald bei Ihnen melden.',
      enterResponse: 'Geben Sie hier Ihre Antwort ein...',
      newConsultation: 'Neue Beratung',
      close: 'Schließen',
      technicalSupport: 'VACWAY Technischer Support',
      autoAssistant: 'Automatischer Diagnoseassistent',
      machine: 'Maschine',
      ticketCreating: 'Support-Ticket wird erstellt…',
      ticketCreated: '✅ Ticket erfolgreich erstellt. Verfolgen Sie es unter',
      ticketCreatedLink: 'Support → Meine Tickets',
      ticketError: '❌ Das Ticket konnte nicht automatisch erstellt werden. Bitte erstellen Sie es manuell unter Support → Meine Tickets.',
    },
    it: {
      incidentCreated: 'Incidente creato con successo. Un tecnico ti contatterà presto.',
      enterResponse: 'Scrivi la tua risposta qui...',
      newConsultation: 'Nuova consultazione',
      close: 'Chiudi',
      technicalSupport: 'Supporto tecnico VACWAY',
      autoAssistant: 'Assistente diagnostico automatico',
      machine: 'Macchina',
      ticketCreating: 'Creazione del ticket in corso…',
      ticketCreated: '✅ Ticket creato con successo. Segui il suo stato in',
      ticketCreatedLink: 'Supporto → I miei ticket',
      ticketError: '❌ Impossibile creare il ticket automaticamente. Creane uno manualmente in Supporto → I miei ticket.',
    },
    pt: {
      incidentCreated: 'Incidente criado com sucesso. Um técnico entrará em contato em breve.',
      enterResponse: 'Digite sua resposta aqui...',
      newConsultation: 'Nova consulta',
      close: 'Fechar',
      technicalSupport: 'Suporte técnico VACWAY',
      autoAssistant: 'Assistente de diagnóstico automático',
      machine: 'Máquina',
      ticketCreating: 'Criando ticket de suporte…',
      ticketCreated: '✅ Ticket criado com sucesso. Acompanhe em',
      ticketCreatedLink: 'Suporte → Meus Tickets',
      ticketError: '❌ Não foi possível criar o ticket automaticamente. Crie um manualmente em Suporte → Meus Tickets.',
    }
  };

  const labels = uiLabels[currentLanguage] || uiLabels['es'];

  // Memoize current node to prevent unnecessary recalculations
  const currentNode = useMemo(() => {
    return getNode(currentNodeId, currentLanguage);
  }, [currentNodeId, currentLanguage]);

  const requiresInput = useMemo(() => {
    return currentNode.options?.some(opt => opt.requiresInput) || false;
  }, [currentNode]);

  const machineInputOption = useMemo(() => {
    if (!conversationHistory.length) return null;
    const last = conversationHistory[conversationHistory.length - 1];
    if (!last?.options) return null;
    const idx = last.options.findIndex(opt => opt.requiresInput && opt.inputType === 'machine');
    return idx >= 0 ? { index: idx, option: last.options[idx] } : null;
  }, [conversationHistory]);

  // JSX variable (not a sub-component) — avoids unmount/remount blink on re-render
  const chatContent = (
    <div className="sc-chat">

      {/* ── LEFT: conversation ── */}
      <div className="sc-chat-main">
        <div ref={chatContainerRef} className="sc-messages">
          {conversationHistory.map((item) => (
            <div
              key={item.id}
              className={`sc-message-row sc-message-row--${item.isBot ? 'bot' : 'user'}`}
            >
              {item.isBot && (
                <div className="sc-avatar">
                  <i className="fa fa-headphones" />
                </div>
              )}
              <div className={`sc-bubble sc-bubble--${item.isBot ? 'bot' : 'user'}`}>
                <div>{item.message}</div>
                {item.additionalInfo && (
                  <div className="sc-bubble-extra">
                    {currentLanguage === 'es' ? 'Información adicional' :
                     currentLanguage === 'en' ? 'Additional information' :
                     currentLanguage === 'fr' ? 'Informations supplémentaires' :
                     currentLanguage === 'de' ? 'Zusätzliche Informationen' :
                     currentLanguage === 'it' ? 'Informazioni aggiuntive' :
                     currentLanguage === 'pt' ? 'Informação adicional' :
                     'Información adicional'}: {item.additionalInfo}
                  </div>
                )}
                <div className="sc-bubble-time">{formatTime(item.timestamp)}</div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="sc-typing-row">
              <div className="sc-avatar">
                <i className="fa fa-headphones" />
              </div>
              <div className="sc-typing-bubble">
                <span className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            </div>
          )}

          {ticketStatus === 'creating' && (
            <div className="sc-ticket-banner sc-ticket-banner--creating">
              <i className="fa fa-spinner fa-spin" />
              {' '}{labels.ticketCreating}
            </div>
          )}
          {ticketStatus === 'created' && (
            <div className="sc-ticket-banner sc-ticket-banner--success">
              <span>{labels.ticketCreated}{' '}</span>
              <Link to="/supportticketadd">{labels.ticketCreatedLink}</Link>.
            </div>
          )}
          {ticketStatus === 'error' && (
            <div className="sc-ticket-banner sc-ticket-banner--error">
              {labels.ticketError}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* ── Options / input area ── */}
        <div className="sc-input-area">
            {conversationHistory.length > 0 &&
             !conversationHistory[conversationHistory.length - 1].isEndNode &&
             conversationHistory[conversationHistory.length - 1].options?.length > 0 && (
              <>
                {machineInputOption ? (
                  <>
                    <p className="sc-machine-picker-label">{machineInputOption.option.text}</p>
                    <div className="sc-options-grid" style={{ gridTemplateColumns: '1fr' }}>
                      {(machines || []).map(m => (
                        <button
                          key={m._id}
                          className="sc-option-btn sc-machine-btn"
                          disabled={isLoading}
                          onClick={() => handleOptionClick(machineInputOption.index, m.Serial)}
                        >
                          <i className="fa fa-hdd-o" style={{ marginRight: '0.5rem' }} />
                          {m.Serial}
                        </button>
                      ))}
                      {(!machines || machines.length === 0) && (
                        <Input
                          type="text"
                          placeholder="Número de serie de la máquina..."
                          value={userInput}
                          onChange={e => setUserInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && userInput.trim() && handleOptionClick(machineInputOption.index)}
                          autoFocus
                        />
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {requiresInput && (
                      <Input
                        type="textarea"
                        placeholder={labels.enterResponse}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        style={{ marginBottom: '0.75rem' }}
                        rows="2"
                        autoFocus
                      />
                    )}
                    <div
                      className="sc-options-grid"
                      style={{ gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))' }}
                    >
                      {conversationHistory[conversationHistory.length - 1].options.map((option, index) => (
                        <button
                          key={index}
                          className="sc-option-btn"
                          onClick={() => handleOptionClick(index)}
                          disabled={isLoading}
                        >
                          {option.text}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {conversationHistory.length > 0 &&
             conversationHistory[conversationHistory.length - 1].isEndNode && (
              <div className="sc-end-actions">
                <Button color="success" onClick={handleRestart}>
                  <i className="fa fa-refresh mr-2"></i>
                  {labels.newConsultation}
                </Button>
                <Button color="secondary" onClick={onClose}>
                  <i className="fa fa-times mr-2"></i>
                  {labels.close}
                </Button>
              </div>
            )}
          </div>
      </div>

    </div>
  );

  if (!isModal) {
    // Used within multi-tab view (no header)
    if (hideHeader) {
      return (
        <div className="sc-inline">
          {chatContent}
        </div>
      );
    }

    // Standalone full-page view
    return (
      <div className="sc-page">
        <Card>
          <CardHeader className="sc-card-header">
            <div>
              <h4>
                <i className="fa fa-headphones mr-2"></i>
                {labels.technicalSupport}
              </h4>
              <small>{labels.autoAssistant}</small>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {machineId && (
                <span className="badge-machine">
                  {labels.machine}: {machineId}
                </span>
              )}
              <Button color="light" size="sm" onClick={onClose}>
                <i className="fa fa-times"></i>
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {chatContent}
          </CardBody>
        </Card>
      </div>
    );
  }

  // Floating modal popup
  return (
    <div className="sc-modal-wrapper">
      <Card>
        <CardHeader className="sc-card-header">
          <div>
            <h5>
              <i className="fa fa-headphones mr-2"></i>
              {labels.technicalSupport.replace(' VACWAY', '')}
            </h5>
            {machineId && (
              <small>{labels.machine}: {machineId}</small>
            )}
          </div>
          <Button close onClick={onClose} style={{ color: 'white', opacity: 1 }}>
            <span aria-hidden="true">×</span>
          </Button>
        </CardHeader>
        <CardBody>
          {chatContent}
        </CardBody>
      </Card>
    </div>
  );
};

const mapStateToProps = state => ({
  machines: state.machines.machines,
  auth: state.auth,
});

const mapDispatchToProps = dispatch => ({
  fetchMachines: bindActionCreators(fetchMachinesAction, dispatch),
  addSupportTicket: bindActionCreators(addSupportTicketAction, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TechnicalSupportChat);
